/**
 * /monday-brief — schedule + preview the weekly executive digest.
 */
import React, { useEffect, useState } from 'react';
import { Mail, Calendar, Send } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import BrandTitle from '../components/BrandTitle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const MondayBrief = () => {
  const [schedule, setSchedule] = useState({ enabled: false, day_of_week: 1, hour_local: 8, timezone_label: 'UTC', deliver_to: '' });
  const [preview, setPreview] = useState(null);
  const [testEmail, setTestEmail] = useState('');
  const [busy, setBusy] = useState(false);

  const headers = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
  });

  const load = async () => {
    try {
      const [s, p] = await Promise.all([
        fetch(`${API}/monday-brief/schedule`, { headers: headers() }).then((r) => r.json()),
        fetch(`${API}/monday-brief/preview`, { headers: headers() }).then((r) => r.json()),
      ]);
      setSchedule(s);
      setPreview(p);
      setTestEmail(s.deliver_to || '');
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { load(); }, []);

  const saveSchedule = async () => {
    setBusy(true);
    const r = await fetch(`${API}/monday-brief/schedule`, { method: 'POST', headers: headers(), body: JSON.stringify(schedule) });
    setBusy(false);
    if (r.ok) toast.success('Schedule saved');
    else toast.error('Failed to save schedule');
  };

  const sendTest = async () => {
    if (!testEmail) { toast.error('Enter an email'); return; }
    setBusy(true);
    const r = await fetch(`${API}/monday-brief/send-test`, { method: 'POST', headers: headers(), body: JSON.stringify({ deliver_to: testEmail }) });
    const j = await r.json();
    setBusy(false);
    if (j.ok) toast.success(`Sent · delivery id ${j.delivery_id || '(unknown)'}`);
    else toast.error(`Couldn't send: ${j.error || 'unknown'}`);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="monday-brief-page">
      <main className="max-w-5xl mx-auto px-6 py-10">
        <Badge className="bg-emerald-500/15 border-emerald-300 text-emerald-300 px-3 py-1 text-[11px] tracking-[0.15em] uppercase font-semibold mb-3">
          <Mail className="w-3 h-3 mr-1.5 inline" /> Monday Morning Brief
        </Badge>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2"><BrandTitle>Monday Morning Brief</BrandTitle></h1>
        <p className="text-slate-300 mb-8 max-w-2xl">
          Scheduled executive digest that lands in Slack/email before Monday stand-up. Last week's wins,
          open critical anomalies, and live burn rate — all in one card-sized email.
        </p>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Schedule editor */}
          <Card className="bg-slate-900/60 border-white/10 shadow-sm">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-emerald-500" />
                <h2 className="font-bold text-white">Schedule</h2>
              </div>

              <label className="flex items-center gap-2 cursor-pointer" data-testid="mb-enabled-toggle">
                <input type="checkbox" checked={!!schedule.enabled} onChange={(e) => setSchedule({ ...schedule, enabled: e.target.checked })} className="w-4 h-4 text-emerald-500" />
                <span className="text-sm font-medium text-slate-200">Enable weekly delivery</span>
              </label>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-1 block">Day</label>
                  <select value={schedule.day_of_week} onChange={(e) => setSchedule({ ...schedule, day_of_week: parseInt(e.target.value, 10) })} className="w-full rounded-lg border border-white/15 px-3 py-2 text-sm" data-testid="mb-day-select">
                    {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-1 block">Hour (local)</label>
                  <input type="number" min={0} max={23} value={schedule.hour_local} onChange={(e) => setSchedule({ ...schedule, hour_local: parseInt(e.target.value, 10) || 0 })} className="w-full rounded-lg border border-white/15 px-3 py-2 text-sm" data-testid="mb-hour-input" />
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-1 block">Deliver to</label>
                <input type="email" value={schedule.deliver_to || ''} onChange={(e) => setSchedule({ ...schedule, deliver_to: e.target.value })} className="w-full rounded-lg border border-white/15 px-3 py-2 text-sm" placeholder="you@company.com" data-testid="mb-email-input" />
              </div>

              <div className="flex gap-2 pt-2">
                <Button onClick={saveSchedule} disabled={busy} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 flex-1" data-testid="mb-save-btn">Save schedule</Button>
              </div>

              <div className="pt-4 border-t border-white/10">
                <label className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-1 block">Send a one-off test</label>
                <div className="flex gap-2">
                  <input type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} className="flex-1 rounded-lg border border-white/15 px-3 py-2 text-sm" placeholder="test@company.com" data-testid="mb-test-email" />
                  <Button onClick={sendTest} disabled={busy} variant="outline" className="border-emerald-300 text-emerald-300 hover:bg-emerald-500/10" data-testid="mb-test-send">
                    <Send className="w-3.5 h-3.5 mr-1" /> Send test
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Live preview */}
          <Card className="bg-slate-900/60 border-white/10 shadow-sm overflow-hidden">
            <CardContent className="p-5">
              <h2 className="font-bold text-white mb-3 flex items-center gap-2">
                <Mail className="w-4 h-4 text-emerald-500" /> Live preview
              </h2>
              {preview ? (
                <div className="rounded-lg overflow-hidden border border-white/10 bg-slate-900/60">
                  <iframe srcDoc={preview.html} className="w-full h-[460px] border-0" title="brief-preview" data-testid="mb-preview-iframe" />
                </div>
              ) : (
                <p className="text-sm text-slate-500">Loading preview…</p>
              )}
              <p className="text-xs text-slate-500 mt-3">This is what every recipient sees Monday morning. Dynamic — pulls live tenant data on each delivery.</p>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default MondayBrief;
